/**
 * Resolves the widget runtime config for an instance.
 *
 * Always calls `GET /api/v1/widget/config` on ochatbot-v2 with
 * `master_key` and/or `agentId`, plus `currentUrl` + `deviceType` for
 * correct AI configuration branch selection.
 */

export async function fetchWidgetConfig({
  apiBaseUrl,
  agentId,
  masterKey,
  inlineConfig,
  signal,
} = {}) {
  const canRemote = Boolean(agentId || masterKey)
  // Inline-only bootstrap (no credentials to call the config API).
  if (inlineConfig && typeof inlineConfig === 'object' && !canRemote) {
    return { payload: inlineConfig, source: 'inline' }
  }

  const base = (apiBaseUrl || '').replace(/\/$/, '')

  if (!canRemote) {
    throw new Error(
      'Widget config requires either `agentId` or `masterKey` to be set on the loader script tag.',
    )
  }

  const url = `${base}/api/v1/widget/config?${buildQuery({
    agentId,
    master_key: masterKey,
    deviceType: widgetConfigDeviceType(),
    currentUrl: typeof window !== 'undefined' ? window.location.href : '',
  })}`
  const json = await fetchJson(url, { signal })
  return { payload: json, source: 'v2' }
}

async function fetchJson(url, init) {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'omit',
    headers: { Accept: 'application/json' },
    ...init,
  })
  if (!response.ok) {
    const error = new Error(
      `Widget config request failed: ${response.status} ${response.statusText}`,
    )
    error.status = response.status
    throw error
  }
  return response.json()
}

function buildQuery(params) {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === '') continue
    search.append(key, String(value))
  }
  return search.toString()
}

export function detectDeviceType() {
  if (typeof navigator === 'undefined') return 'desktop'
  const ua = navigator.userAgent || ''
  if (/iPad|Tablet/i.test(ua)) return 'tablet'
  if (/Mobi|Android/i.test(ua)) return 'mobile'
  return 'desktop'
}

/**
 * Device branch for widget **config** fetches. Must match the same
 * breakpoint as `widget.js` / `LayoutManager` (`max-width: 767px`), not
 * raw user-agent "tablet" / "mobile", or the API reads the wrong
 * `desktop` vs `mobile` block from `ometrics_ai_configuration` and
 * omits `theme.layout` (floating defaults) — which makes the UI fall
 * back to centered overlay (`panel`) even when desktop is set to slide-out.
 */
export function widgetConfigDeviceType() {
  if (typeof window === 'undefined') return 'desktop'
  return window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop'
}
